import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import backGroundScreen from "../../../img/background_screen.png";
import { useSearchParams } from "react-router-dom";
import DanmakuComment from "../../../shared/components/user/comment/DanmakuComment";
import useWebSocket from "../../../shared/hooks/communication/useWebSocket";
import type { ScreenDisplaySettings } from "./ScreenSettings";
import { getBaseUrl } from "../../../config/urls";

// ルームIDを受け取るための型を定義
type LocationState = {
  roomid: string;
};

type NextImageResponse = {
  ret: string;
  msg: string;
  image: string;
};

type ImageItem = {
  id: string;
  base64: string;
  position: { top: string; left: string };
  style: { size: string; rotation: string };
  animationName: string;
};

const Screen = () => {
    const [searchParams] = useSearchParams();
    const roomid = searchParams.get("roomid")!;
    // WebSocket接続でコメントを受信
    const { isConnected, comments, clearComments } = useWebSocket(roomid);
    const [displayComments, setDisplayComments] = useState<any[]>([]);
    const processedCommentIdsRef = useRef<Set<number>>(new Set());

    // --- パフォーマンス計測用のRef ---
    const startTimeRef = useRef<number | null>(null);
    const lastImageIdRef = useRef<string | null>(null);
    const initialMountTimeRef = useRef<number | null>(null);
    const firstAnimationLoggedRef = useRef(false);
    const isFirstIntervalRef = useRef(true); // 初回インターバル用フラグ

    // 設定管理
    const [settings, setSettings] = useState<ScreenDisplaySettings>({
        onlyModerated: true,
        displayInterval: 4000,
        refreshInterval: 5000
    });

    const [images, setImages] = useState<{ id: string; base64: string }[]>([]); // 画像のIDとBase64形式のデータ
    const [positions, setPositions] = useState<{ top: string; left: string }[]>([]); // 各画像の位置
    const [styles, setStyles] = useState<{ size: string; rotation: string }[]>([]); // 各画像のサイズと回転情報
    const [dynamicStyles, setDynamicStyles] = useState<{ [key: string]: string }>({}); // 個別の動的なCSS
    const isProcessing = useRef(false); // 画像処理中フラグ
    const nextPositionRef = useRef<{ top: string; left: string } | null>(null); // 現在の`newPosition` を保持する
    const nextImageSizeRef = useRef<number | null>(null); // 現在の`currentImageSize` を保持する

    const center = { top: "50%", left: "50%" };

    // バックエンドAPIのURL
    const urlBase = getBaseUrl();

    // apiのURL
    const urlNextImage = urlBase + "/api/picture/next";
    const urlNewImagesList = urlBase + "/api/picture/fetchNewImagesList"

    // --- 初回マウント時の時刻を記録 ---
    useEffect(() => {
        if (!initialMountTimeRef.current) {
            initialMountTimeRef.current = performance.now();
        }
    }, []);

    // 設定をlocalStorageから読み込み
    useEffect(() => {
        const savedSettings = localStorage.getItem(`screen-settings-${roomid}`);
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setSettings(parsed);
                console.log('[Screen] 設定を読み込みました:', parsed);
                
                // 設定変更時に表示中の画像をクリア（承認状態が変わった画像を除去）
                setImages([]);
                setPositions([]);
                setStyles([]);
                setDynamicStyles({});
                
            } catch (error) {
                console.error('[Screen] 設定の読み込みに失敗:', error);
            }
        }
    }, [roomid]);

    // 設定が変更された際の処理（localStorage変更の監視）
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === `screen-settings-${roomid}` && e.newValue) {
                try {
                    const newSettings = JSON.parse(e.newValue);
                    console.log('[Screen] 設定が外部で変更されました:', newSettings);
                    setSettings(newSettings);
                    
                    // 設定変更時に表示中の画像をクリア
                    setImages([]);
                    setPositions([]);
                    setStyles([]);
                    setDynamicStyles({});
                    
                } catch (error) {
                    console.error('[Screen] 外部設定変更の読み込みに失敗:', error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [roomid]);

    // アニメーション開始時間を計測するuseEffect
    useEffect(() => {
        // --- 初回アニメーション開始時間の計測 ---
        if (images.length > 0 && !firstAnimationLoggedRef.current && initialMountTimeRef.current) {
            const firstAnimationStartTime = performance.now();
            const durationFromMount = (firstAnimationStartTime - initialMountTimeRef.current) / 1000;
            console.log(`[初回パフォーマンス] 画面表示から初回アニメーション開始まで: ${durationFromMount.toFixed(2)} 秒`);
            firstAnimationLoggedRef.current = true; // 記録したフラグを立てる
        }

        // --- 各画像のアニメーション開始時間の計測 ---
        if (images.length > 0 && startTimeRef.current && lastImageIdRef.current === images[images.length - 1].id) {
            const animationStartTime = performance.now();
            const duration = (animationStartTime - startTimeRef.current) / 1000;
            console.log(`[パフォーマンス] アニメーション開始: ${duration.toFixed(2)} 秒 (計測開始から)`);
            // 一度ログを出したらリセットして、不要な再ログを防ぐ
            lastImageIdRef.current = null; 
        }
    }, [images]);

    const gridPositionsArray = useMemo(() => {
        const gridRows = 3; // グリッドの行数
        const gridCols = 4; // グリッドの列数
    
        // グリッド位置を生成してランダムに並べ替え
        const positionsArray: { top: string; left: string }[] = Array.from(
            { length: gridRows * gridCols },
            (_, index) => ({
                top: `${(100 / gridRows) * Math.floor(index / gridCols)}%`,
                left: `${(100 / gridCols) * (index % gridCols)}%`,
            })
        );

        for (let i = positionsArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [positionsArray[i], positionsArray[j]] = [positionsArray[j], positionsArray[i]];
        }

        return positionsArray;
    }, []);

    // ランダムなサイズを生成する関数
    const generateRandomSize = (): number => {
        const rand = Math.random();
        if (rand <= 0.50) {
            return Math.random() * (22 - 18) + 18;
        } else if (rand <= 0.85) {
            return Math.random() * (27 - 23) + 23;
        } else {
            return Math.random() * (32 - 28) + 28;
        }
    };


    // サーバーから次の画像を取得する関数
    const fetchNextImage = useCallback(async () => {
      // 処理中の場合は新しいリクエストを拒否
      if (isProcessing.current) {
        return;
      }

      isProcessing.current = true;
      try {
        startTimeRef.current = performance.now(); // ★ 計測開始時刻をRefに保存
        const token = localStorage.getItem("token");
        console.log(`[Screen-fetchNextImage] API call with onlyModerated=${settings.onlyModerated}`);
        const response = await axios.get<NextImageResponse>(`${urlNextImage}?roomId=${roomid}&onlyModerated=${settings.onlyModerated}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const nextImageBase64 = response.data.image;

        if (response.data.ret === "true" && nextImageBase64) {
          if (startTimeRef.current) {
            const endTime = performance.now();
            const loadTime = (endTime - startTimeRef.current) / 1000;
            console.log(`[パフォーマンス] 画像読み込み完了: ${loadTime.toFixed(2)} 秒`);
          }

          const newImageId = `image-${Date.now()}`;
          lastImageIdRef.current = newImageId; // ★ 最後に追加された画像のIDを記録

                // 画像リスト (images) を更新
                setImages((prevImages) => {
                    const updatedImages = [...prevImages]; // 現在の画像リストをコピー
                    if (updatedImages.length >= gridPositionsArray.length) {
                        // 画像の数がグリッドの最大数を超えた場合、最初の画像を削除
                        updatedImages.shift();
                    }
                    updatedImages.push({ id: newImageId, base64: nextImageBase64 }); // 新しい画像をリストに追加
                    return updatedImages; // 更新された画像リストを返す
                });

                // 画像の配置位置 (positions) を更新
                setPositions((prevPositions) => {
                    const updatedPositions = [...prevPositions]; // 現在の位置リストをコピー

                    if (updatedPositions.length >= gridPositionsArray.length) {
                        updatedPositions.shift(); // 古い位置を削除
                    }

                    const unusedPositions = gridPositionsArray.filter(
                        (pos) => !updatedPositions.some((used) => used.top === pos.top && used.left === pos.left)
                    );

                    const nextPosition =
                        unusedPositions.length > 0
                            ? unusedPositions[Math.floor(Math.random() * unusedPositions.length)]
                            : { top: "50%", left: "50%" }; // 空いている位置がなければ中央に配置

                    nextPositionRef.current = nextPosition; // 新しい位置を nextPositionRef に格納
                    updatedPositions.push(nextPosition); // 新しい位置をリストに追加
                    return updatedPositions; // 更新された位置リストを返す
                });

                // 画像のスタイル (styles) を更新
                setStyles((prevStyles) => {
                    const currentImageSize = generateRandomSize(); // ランダムなサイズを生成
                    const randomRotation = Math.random() * 30 - 15; // -15度から15度のランダムな回転角度を生成

                    const updatedStyles = [...prevStyles]; // 現在のスタイルリストをコピー
                    if (updatedStyles.length >= gridPositionsArray.length) {
                        updatedStyles.shift(); // スタイルの数がグリッドの最大数を超えた場合、最初のスタイルを削除
                    }

                    nextImageSizeRef.current = currentImageSize; // 生成したサイズを nextImageSizeRef に格納

                    updatedStyles.push({
                        size: `${currentImageSize}%`, // ランダムサイズを % 単位で適用
                        rotation: `${randomRotation}deg`, // ランダム回転を deg 単位で適用
                    });
                    return updatedStyles; // 更新されたスタイルリストを返す
                });

                // 動的なCSSを追加
                setDynamicStyles((prevStyles) => {
                    const animationName = `move-to-grid-${newImageId}`; // 一意なIDをアニメーション名に使用

                    if (nextPositionRef.current && nextImageSizeRef.current) {
                        const nextPositionTopPercent = parseFloat(nextPositionRef.current.top);
                        const nextPositionLeftPercent = parseFloat(nextPositionRef.current.left);

                        const nextPositionTopPx = (nextPositionTopPercent / 100) * window.innerHeight;
                        const nextPositionLeftPx = (nextPositionLeftPercent / 100) * window.innerWidth;

                        const halfCurrentImageSizePx =
                            (nextImageSizeRef.current / 100) * window.innerWidth / 2;

                        const centerTopPx = window.innerHeight * 0.5;
                        const centerLeftPx = window.innerWidth * 0.5;

                        const startTop = window.innerHeight - nextPositionTopPx - halfCurrentImageSizePx;;
                        const translateTop = centerTopPx - nextPositionTopPx - halfCurrentImageSizePx;
                        const translateLeft = centerLeftPx - nextPositionLeftPx - halfCurrentImageSizePx;

                        const newRotation = Math.random() * 40 - 15; // ランダム回転角度

                        const newAnimation = `
                            @keyframes ${animationName} {
                                0% {
                                    transform: translate(${translateLeft}px, ${startTop}px) scale(2) rotate(0deg) rotateX(0deg) rotateY(0deg);
                                }
                                20% {
                                    transform: translate(${translateLeft}px, ${translateTop}px) scale(2) rotate(0deg) rotateX(0deg) rotateY(0deg);
                                }
                                30% {
                                    transform: translate(${translateLeft}px, ${translateTop}px) scale(2) rotate(0deg) rotateX(0deg) rotateY(0deg);
                                }
                                60% {
                                    transform: translate(${translateLeft * 0.5}px, ${translateTop * 0.5}px) scale(1.5) translateX(-30px) rotate(20deg) rotateX(110deg);
                                }
                                80% {
                                    transform: translate(${translateLeft * 0.2}px, ${translateTop * 0.2}px) scale(1.2) translateX(30px) rotate(-20deg) rotateX(60deg);
                                }
                                100% {
                                    transform: translate(0, 0) scale(1) rotate(${newRotation}deg);
                                }
                            }
                        `;

                        return { ...prevStyles, [animationName]: newAnimation };
                    }

                    return prevStyles; // nextPositionRef.current が null の場合は何も追加しない
                });

            } else {
                fetchNewImagesList();
            }
        } catch (error) {
            console.error("画像の取得に失敗しました:", error);
        } finally {
            isProcessing.current = false;
        }
    }, [gridPositionsArray, roomid, settings.onlyModerated]);

    useEffect(() => {
        isFirstIntervalRef.current = true; // 依存関係の変更時にリセット
        let timer: NodeJS.Timeout;

        const loop = () => {
            const currentInterval = isFirstIntervalRef.current ? 100 : settings.displayInterval;
            console.log(`[Screen] 次の画像取得を${currentInterval}ms後にスケジュール`);

            timer = setTimeout(() => {
                fetchNextImage().finally(() => {
                    if (isFirstIntervalRef.current) {
                        isFirstIntervalRef.current = false;
                    }
                    // 次のループをスケジュール
                    loop();
                });
            }, currentInterval);
        };

        loop(); // ループを開始

        return () => {
            clearTimeout(timer); // コンポーネントのアンマウント時や再実行時にタイマーをクリア
        };
    }, [settings.displayInterval, fetchNextImage]);

    // サーバーにDBから再取得するよう指示する処理
    const fetchNewImagesList = async () => {
        try {
            const token = localStorage.getItem("token");
            console.log(`[Screen-fetchNewImagesList] API call with onlyModerated=${settings.onlyModerated}`);
            const response = await axios.get(`${urlNewImagesList}?roomId=${roomid}&onlyModerated=${settings.onlyModerated}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // サーバーがvoidを返すため、response.dataは基本的に空
            if (response.status === 200) {
                console.log("Successfully requested image retrieval.");
            }
        } catch (error) {
            console.error("Error fetching images:", error);
        }
    };

    useEffect(() => {
        const intervalId = setInterval(fetchNewImagesList, settings.refreshInterval);
        console.log(`[Screen] 新規画像チェック間隔を${settings.refreshInterval}msに設定`);

        // コンポーネントのアンマウント時にクリア
        return () => clearInterval(intervalId);
    }, [settings.refreshInterval, settings.onlyModerated]); // 設定が変更されたら再設定
    
    // WebSocketコメントを表示用リストに追加（重複を防ぐ）
    useEffect(() => {
        if (comments.length > 0) {
            const newComments = comments.filter(comment => 
                !processedCommentIdsRef.current.has(comment.commentid)
            );
            
            if (newComments.length > 0) {
                console.log('[Screen] 新しいコメントを表示:', newComments);
                setDisplayComments(prev => [...prev, ...newComments]);
                newComments.forEach(comment => 
                    processedCommentIdsRef.current.add(comment.commentid)
                );
            }
        }
    }, [comments]);

    // コメント完了時のハンドラー
    const handleCommentComplete = useCallback((commentId: number) => {
        setDisplayComments(prev => prev.filter(comment => comment.commentid !== commentId));
    }, []);

    // コンポーネントアンマウント時のクリーンアップ
    useEffect(() => {
        return () => {
            processedCommentIdsRef.current.clear();
        };
    }, []);

    if (!roomid) {
        return <div>ルームIDが指定されていません。</div>;
    }


    return (
        <div
            className="App"
            style={{
                backgroundImage: `url(${backGroundScreen})`,
                backgroundSize: "100vw 100vh",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                overflow: "hidden",
            }}
        >
            {/* 動的なスタイルを適用 */}
            <style
            dangerouslySetInnerHTML={{
                __html: Object.values(dynamicStyles).join("\n"),
            }}
            />
            <div
                className="gallery-container"
                style={{ position: "relative", width: "100%", height: "100vh" }}
            >
                {images.map(({ id, base64 }, index) => {
                    const position = positions[index] || center; // 画像の最初の位置を中央に設定
                    const style = styles[index] || { size: "20vw", rotation: "0deg" };

                    return (
                        <div
                            key={id} // 一意なIDをkeyに使用
                            className="photo-frame"
                            style={{
                                position: "absolute",
                                top: position.top,
                                left: position.left,
                                width: style.size,
                                height: style.size,
                                transform: `rotate(${style.rotation})`,
                                transition: "all 1s ease-in-out",
                                animation: `move-to-grid-${id} 3s forwards`, // ---- アニメーションが終わるまでの時間 ---- ▶︎ 3s = 3秒
                            }}
                            onAnimationEnd={() => { // ★ アニメーション終了ハンドラ
                                if (startTimeRef.current) {
                                    const animationEndTime = performance.now();
                                    const duration = (animationEndTime - startTimeRef.current) / 1000;
                                    console.log(`[パフォーマンス] 画像 ${id} のアニメーション終了: ${duration.toFixed(2)} 秒 (計測開始から)`);
                                }
                            }}
                        >
                            <img
                                src={`data:image/png;base64,${base64}`}
                                alt="Next Image"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "5px",
                                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                                    background: "rgb(248,246,236)",
                                    padding: "6%",
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            
            {/* 弾幕コメント表示エリア */}
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                {displayComments.map((comment) => (
                    <DanmakuComment
                        key={comment.commentid}
                        comment={comment}
                        onComplete={handleCommentComplete}
                    />
                ))}
            </div>
            
        </div>
    );
};

export default Screen;